/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Check if localStorage is available and privacy consent allows it
   * @returns {boolean}
   */
  static #canUseLocalStorage() {
    try {
      if (!window.Shopify?.customerPrivacy) {
        // Privacy API not loaded yet, allow localStorage
        return true;
      }
      
      // Check if analytics consent is granted
      const consent = window.Shopify.customerPrivacy.currentVisitorConsent();
      return consent?.analytics || consent?.marketing || true; // Default to true if no consent object
    } catch (e) {
      return false;
    }
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    if (!this.#canUseLocalStorage()) return;

    try {
      let viewedProducts = this.getProducts();

      viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
      viewedProducts.unshift(productId);
      viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch (e) {
      console.warn('Unable to save recently viewed products:', e);
    }
  }

  static clearProducts() {
    if (!this.#canUseLocalStorage()) return;
    
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch (e) {
      console.warn('Unable to clear recently viewed products:', e);
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    if (!this.#canUseLocalStorage()) return [];

    try {
      return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch (e) {
      console.warn('Unable to retrieve recently viewed products:', e);
      return [];
    }
  }
}
